import React, { useState } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import {
  ShoppingBag,
  Calendar,
  BarChart3,
  CreditCard,
  Users,
  Smartphone,
  Sparkles
} from 'lucide-react';
import './App.css';

// API Base URL - uses Vercel rewrites to proxy /api/* to backend
const API_BASE_URL = '';

function App() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/waitlist/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        toast.success('You\'re on the list! We\'ll be in touch soon.');
        setEmail('');
      } else {
        if (data.error?.includes('already')) {
          toast.error('This email is already on our waitlist!');
        } else {
          toast.error(data.error || 'Something went wrong. Please try again.');
        }
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Unable to connect. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: ShoppingBag, label: 'Online Store', position: 'top-left' },
    { icon: CreditCard, label: 'POS System', position: 'top-right' },
    { icon: Calendar, label: 'Appointments', position: 'bottom-left' },
    { icon: BarChart3, label: 'Analytics', position: 'bottom-right' },
    { icon: Users, label: 'Team Management', position: 'left' },
    { icon: Smartphone, label: 'Mobile Ready', position: 'right' },
    { icon: Sparkles, label: 'AI Powered', position: 'center-top' },
  ];

  return (
    <div className="waitlist-container">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#333',
            color: '#fff',
            borderRadius: '10px',
          },
        }}
      />

      {/* Background Elements */}
      <div className="bg-gradient"></div>
      <div className="bg-noise"></div>

      {/* Main Content */}
      <main className="main-content">
        {/* Floating Feature Badges */}
        {features.map((feature, index) => (
          <div
            key={index}
            className={`feature-badge ${feature.position}`}
            style={{ animationDelay: `${index * 0.2}s` }}
          >
            <feature.icon size={16} />
            <span>{feature.label}</span>
          </div>
        ))}

        {/* Animated Circles */}
        <div className="circles-container">
          <div className="circle circle-1"></div>
          <div className="circle circle-2"></div>
          <div className="circle circle-3"></div>
          <div className="circle circle-4"></div>

          {/* Center Logo */}
          <div className="logo-container">
            <img
              src="https://revstar-media-production.s3.eu-north-1.amazonaws.com/website-assets/img/Revstar-Logo.png"
              alt="Revstar Logo"
              className="center-logo"
            />
          </div>
        </div>

        {/* Text Content */}
        <div className="text-content">
          <h1 className="headline">
            <span className="headline-word">Sell.</span>
            <span className="headline-word highlight">Grow.</span>
            <span className="headline-word">Scale.</span>
          </h1>

          <p className="description">
            Running a business in Africa shouldn't be complicated.
            Revstar is the <strong>all-in-one commerce platform</strong> that helps you
            sell products, book appointments, manage inventory, and accept payments —
            all from one place.
          </p>

          {/* Mobile Features List */}
          <div className="mobile-features">
            {features.map((feature, index) => (
              <span key={index} className="mobile-feature-item">
                {feature.label}
              </span>
            ))}
          </div>

          {/* Email Form */}
          {!submitted ? (
            <form onSubmit={handleSubmit} className="email-form">
              <div className="input-wrapper">
                <label htmlFor="email">Your email</label>
                <input
                  type="email"
                  id="email"
                  placeholder="merchant@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
              <button
                type="submit"
                className="submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  'Request early access'
                )}
              </button>
            </form>
          ) : (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <p>You're on the list! We'll reach out when it's your turn.</p>
            </div>
          )}
        </div>

        {/* Social Links */}
        <div className="social-links">
          <a href="https://twitter.com/userevstar" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </a>
          <a href="https://instagram.com/userevstar" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          <a href="https://linkedin.com/company/userevstar" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
        </div>

        {/* Footer */}
        <footer className="footer">
          <span>Built by</span>
          <img
            src="https://revstar-media-production.s3.eu-north-1.amazonaws.com/website-assets/img/Revstar-Logo.png"
            alt="Revstar"
            className="footer-logo"
          />
          <span>Revstar</span>
        </footer>
      </main>
    </div>
  );
}

export default App;
